import AsyncStorage from '@react-native-async-storage/async-storage'
import { Platform } from 'react-native'

/**
 * Cross-platform storage utility that works in both web and native environments.
 * Uses localStorage on web and AsyncStorage on native.
 */
export const storage = {
  /**
   * Gets an item from storage by key
   * @param key The key to retrieve
   * @returns Promise that resolves to the value or null if not found
   */
  getItem(key: string): Promise<string | null> {
    if (Platform.OS === 'web') {
      return Promise.resolve(localStorage.getItem(key))
    }
    return AsyncStorage.getItem(key)
  },

  /**
   * Stores an item in storage
   * @param key The key to store under
   * @param value The string value to store
   * @returns Promise that resolves when the operation is complete
   */
  setItem(key: string, value: string): Promise<void> {
    if (Platform.OS === 'web') {
      localStorage.setItem(key, value)
      return Promise.resolve()
    }
    return AsyncStorage.setItem(key, value)
  },

  /**
   * Removes an item from storage
   * @param key The key to remove
   * @returns Promise that resolves when the operation is complete
   */
  removeItem(key: string): Promise<void> {
    if (Platform.OS === 'web') {
      localStorage.removeItem(key)
      return Promise.resolve()
    }
    return AsyncStorage.removeItem(key)
  },

  /**
   * Clears all items from storage
   * @returns Promise that resolves when the operation is complete
   */
  clear(): Promise<void> {
    if (Platform.OS === 'web') {
      localStorage.clear()
      return Promise.resolve()
    }
    return AsyncStorage.clear()
  },

  /**
   * Utility function to store an object as JSON
   * @param key The key to store under
   * @param value The object to store
   * @returns Promise that resolves when the operation is complete
   */
  setObject<T>(key: string, value: T): Promise<void> {
    return this.setItem(key, JSON.stringify(value))
  },

  /**
   * Utility function to retrieve and parse a JSON object
   * @param key The key to retrieve
   * @param defaultValue The default value to return if not found
   * @returns Promise that resolves to the parsed object or defaultValue if not found
   */
  async getObject<T>(key: string, defaultValue: T): Promise<T> {
    const value = await this.getItem(key)
    if (value === null) {
      return defaultValue
    }
    try {
      return JSON.parse(value) as T
    } catch (error) {
      console.error(`Error parsing stored JSON for key ${key}:`, error)
      return defaultValue
    }
  }
} 